// src/repository/InsightsDAO.ts
//
// The only file in this feature allowed to reference a raw `"Cube.member"` string. Views
// and hooks consume the clean domain objects in InsightsTypes.ts only, so a later swap to
// a BFF (see plan "Known limitations") touches this file plus the transport, nothing else.

import { loadCube, toNumber, toRatio, type CubeRow } from '@/repository/cube/CubeClient';
import {
  NOT_SEEN_OVER_24H_SLICE_LABEL,
  RECENTLY_ACTIVE_SLICE_LABEL,
  SEEN_LAST_24H_SLICE_LABEL,
  UNKNOWN_VERSION_LABEL,
  type CompliancePoint,
  type ComplianceTrendResult,
  type CoreProDeviceCountSummary,
  type DeviceStatusSlice,
  type FeatureComplianceSummary,
  type FirmwareDeviceRow,
  type FleetStatus,
  type ProtectedHoursSummary,
  type UtilizationSummary,
  type VersionBucket,
  type VersionDistribution,
  type WorkZoneBreach,
  type WorkZoneBreachChannel,
  type WorkZoneBreachLocation,
  type WorkZoneBreachQueryResult,
} from '@/models/Insights/InsightsTypes';
import type { InsightsDateRange } from '@/models/Insights/insightsDateRange';

const orgFilter = (orgCode: string) => [
  { member: 'DeviceLatestStatus.org_code', operator: 'equals' as const, values: [orgCode] },
];

const rollupOrgFilter = (orgCode: string) => [
  { member: 'DailyOrgRollup.org_code', operator: 'equals' as const, values: [orgCode] },
];

/** `DailyDeviceRollup`'s own org-code filter - Cube filters are member-qualified per cube,
 * so `rollupOrgFilter` (built against `DailyOrgRollup.org_code`) cannot be reused here. */
const dailyDeviceRollupOrgFilter = (orgCode: string) => [
  { member: 'DailyDeviceRollup.org_code', operator: 'equals' as const, values: [orgCode] },
];

/** `WorkZoneBreach`'s own org-code filter, following `dailyDeviceRollupOrgFilter`'s
 * precedent immediately above: Cube filters are member-qualified per cube, so neither
 * `rollupOrgFilter` nor `dailyDeviceRollupOrgFilter` (built against other cubes' own
 * `org_code` dimensions) can be reused for a `WorkZoneBreach.org_code` filter - each cube
 * needs its own org-filter helper qualified to its own member name. */
const workZoneBreachOrgFilter = (orgCode: string) => [
  { member: 'WorkZoneBreach.org_code', operator: 'equals' as const, values: [orgCode] },
];

/** Current fleet status - all five measures are `now64(3)`-relative with no date filter
 * (Task 4.2), so this is identical at every selected range preset. */
export async function getFleetStatus(orgCode: string): Promise<FleetStatus> {
  const [row] = await loadCube({
    measures: [
      'DeviceLatestStatus.device_count',
      'DeviceLatestStatus.recently_active_device_count',
      'DeviceLatestStatus.reported_today_device_count',
      'DeviceLatestStatus.offline_over_24h_device_count',
      'DeviceLatestStatus.low_battery_device_count',
    ],
    filters: orgFilter(orgCode),
  });

  return {
    totalReportingDevices: toNumber(row?.['DeviceLatestStatus.device_count']),
    recentlyActive: toNumber(row?.['DeviceLatestStatus.recently_active_device_count']),
    reportedTodayUtc: toNumber(row?.['DeviceLatestStatus.reported_today_device_count']),
    offlineOver24h: toNumber(row?.['DeviceLatestStatus.offline_over_24h_device_count']),
    lowBattery: toNumber(row?.['DeviceLatestStatus.low_battery_device_count']),
  };
}

/** Slice label for the donut's exclusive partition (Rule 5). The `1`/`1` combination
 * (recently active AND offline >24h) is impossible per the cube model and is not expected
 * to appear; it falls back to the "Unknown" label defensively rather than throwing. */
function deviceStatusSliceLabel(
  isRecentlyActive: number | null,
  isOfflineOver24h: number | null,
): string {
  if (isRecentlyActive === 1 && isOfflineOver24h === 0) {
    return RECENTLY_ACTIVE_SLICE_LABEL;
  }
  if (isRecentlyActive === 0 && isOfflineOver24h === 0) {
    return SEEN_LAST_24H_SLICE_LABEL;
  }
  if (isRecentlyActive === 0 && isOfflineOver24h === 1) {
    return NOT_SEEN_OVER_24H_SLICE_LABEL;
  }
  return UNKNOWN_VERSION_LABEL;
}

/** The exclusive three-slice donut partition (Rule 5) - never the four overlapping fleet
 * badges. Slices sum exactly to `device_count`. */
export async function getDeviceStatusBreakdown(orgCode: string): Promise<DeviceStatusSlice[]> {
  const rows = await loadCube({
    measures: ['DeviceLatestStatus.device_count'],
    dimensions: ['DeviceLatestStatus.is_recently_active', 'DeviceLatestStatus.is_offline_over_24h'],
    filters: orgFilter(orgCode),
  });

  return rows.map((row) => ({
    label: deviceStatusSliceLabel(
      toNumber(row['DeviceLatestStatus.is_recently_active']),
      toNumber(row['DeviceLatestStatus.is_offline_over_24h']),
    ),
    count: toNumber(row['DeviceLatestStatus.device_count']) ?? 0,
  }));
}

/** Sums one numeric measure key across every row - unit-agnostic (seconds, hours,
 * whatever `key` holds), since a day-grain query's window total is always just the sum of
 * its per-day rows. Renamed from `sumSeconds` when `getProtectedHours` needed the same
 * reduction over an hours-denominated measure - the seconds-specific name would have been
 * misleading applied to `DailyDeviceRollup.valid_reporting_hours`. */
function sumMeasure(rows: CubeRow[], key: string): number {
  return rows.reduce((total, row) => total + (toNumber(row[key]) ?? 0), 0);
}

/** sum(numeratorKey) / sum(validKey), or null when sum(validKey) is 0 (Rule 1). This is
 * deliberately not the mean of the per-row ratios - see ComplianceTrendResult. */
function sumRatio(rows: CubeRow[], numeratorKey: string, validKey: string): number | null {
  const validTotal = sumMeasure(rows, validKey);
  if (validTotal === 0) return null;
  return sumMeasure(rows, numeratorKey) / validTotal;
}

/**
 * Day-grain compliance/mute trend (Rule 2 - `granularity: "day"` is required here), plus
 * whole-window headline ratios recomputed as sum(protected)/sum(valid) and
 * sum(muted)/sum(valid) across the returned rows (Task 2.3). Never request the coverage
 * measure alongside this query (Rule 2) - use `getReportingCoverage` instead.
 */
export async function getComplianceTrend(
  orgCode: string,
  { from, to }: InsightsDateRange,
): Promise<ComplianceTrendResult> {
  const rows = await loadCube({
    measures: [
      'DailyOrgRollup.compliance_ratio',
      'DailyOrgRollup.mute_ratio',
      'DailyOrgRollup.protected_reporting_seconds',
      'DailyOrgRollup.muted_reporting_seconds',
      'DailyOrgRollup.valid_reporting_seconds',
    ],
    filters: rollupOrgFilter(orgCode),
    timeDimensions: [
      { dimension: 'DailyOrgRollup.event_date', dateRange: [from, to], granularity: 'day' },
    ],
    order: { 'DailyOrgRollup.event_date': 'asc' },
  });

  const points: CompliancePoint[] = rows.map((row) => ({
    date: String(row['DailyOrgRollup.event_date']).slice(0, 10),
    complianceRatio: toRatio(row['DailyOrgRollup.compliance_ratio']),
    muteRatio: toRatio(row['DailyOrgRollup.mute_ratio']),
  }));

  return {
    points,
    headlineComplianceRatio: sumRatio(
      rows,
      'DailyOrgRollup.protected_reporting_seconds',
      'DailyOrgRollup.valid_reporting_seconds',
    ),
    headlineMuteRatio: sumRatio(
      rows,
      'DailyOrgRollup.muted_reporting_seconds',
      'DailyOrgRollup.valid_reporting_seconds',
    ),
  };
}

/**
 * Whole-window per-feature safety compliance (arc, fall, head impact), sourced from
 * `DailyOrgRollup`'s per-feature fail-ratio diagnostics. Four rules this function must
 * encode, not just document:
 *
 *  1. Invert the ratio - the cube measures are *fail* ratios (`arc_fail_ratio`, etc.); the
 *     displayed score is `1 - fail_ratio`, computed only when the ratio is non-null. A null
 *     ratio (see `sumRatio`) must propagate as null, never `1 - null`.
 *  2. Non-additive - arc/fall/head are independent per-feature diagnostics (cube model
 *     comment above the per-feature measures). Never sum or otherwise combine these three
 *     scores into a "total" anywhere in this feature.
 *  3. `head_applicable_seconds` sums to 0 for a Core-only org, so `sumRatio` returns null by
 *     construction (divide-by-zero), and so does `headScore`. The caller
 *     (FeatureComplianceSection) renders this with the same generic "not enough data"
 *     caption every other null metric on this page gets - it no longer asserts "Pro-only"
 *     in the UI, since the SQL/Cube gate behind `head_applicable_seconds` still nulls this
 *     out for Core devices for reasons unrelated to genuine missing data (a correction not
 *     yet made here - see FeatureComplianceSection.tsx's own comment at that call site).
 *  4. Window headline is sum-over-sum, not an average of daily ratios - day-grain rows are
 *     requested (matching `getComplianceTrend`'s pattern) purely so the six sums can be
 *     reduced with the existing `sumRatio` helper across the whole window; no day-grain
 *     points are returned since only the three headline numbers are needed here.
 */
export async function getFeatureCompliance(
  orgCode: string,
  { from, to }: InsightsDateRange,
): Promise<FeatureComplianceSummary> {
  const rows = await loadCube({
    measures: [
      'DailyOrgRollup.arc_applicable_seconds',
      'DailyOrgRollup.arc_fail_seconds',
      'DailyOrgRollup.fall_applicable_seconds',
      'DailyOrgRollup.fall_fail_seconds',
      'DailyOrgRollup.head_applicable_seconds',
      'DailyOrgRollup.head_fail_seconds',
    ],
    filters: rollupOrgFilter(orgCode),
    timeDimensions: [
      { dimension: 'DailyOrgRollup.event_date', dateRange: [from, to], granularity: 'day' },
    ],
    order: { 'DailyOrgRollup.event_date': 'asc' },
  });

  const arcFailRatio = sumRatio(
    rows,
    'DailyOrgRollup.arc_fail_seconds',
    'DailyOrgRollup.arc_applicable_seconds',
  );
  const fallFailRatio = sumRatio(
    rows,
    'DailyOrgRollup.fall_fail_seconds',
    'DailyOrgRollup.fall_applicable_seconds',
  );
  const headFailRatio = sumRatio(
    rows,
    'DailyOrgRollup.head_fail_seconds',
    'DailyOrgRollup.head_applicable_seconds',
  );

  return {
    arcScore: arcFailRatio === null ? null : 1 - arcFailRatio,
    fallScore: fallFailRatio === null ? null : 1 - fallFailRatio,
    headScore: headFailRatio === null ? null : 1 - headFailRatio,
  };
}

/**
 * Earliest `event_date` this org has any `DailyOrgRollup` row for, or `null` when the org
 * has zero rows (never seeded, or genuinely brand new). Feeds the Custom range picker's
 * lower bound (Part B) only - never called for a fixed preset. Uses `dimensions`, not
 * `timeDimensions`, deliberately: this query has no date window of its own, and
 * `CubeTimeDimension.dateRange` is a required field, so a `timeDimensions` entry here would
 * force a bound we don't have yet. Slices the first 10 characters off the returned
 * timestamp string rather than parsing it into a `Date` and re-serializing, which would
 * reintroduce the same UTC-instant/local-timezone trap `formatPickerDate` exists to avoid
 * (Trap 2). Callers decide the null/error fallback (see `useEarliestDataDate`) - this
 * function does not catch and does not guess.
 */
export async function getEarliestDataDate(orgCode: string): Promise<string | null> {
  const [row] = await loadCube({
    measures: ['DailyOrgRollup.event_count'],
    dimensions: ['DailyOrgRollup.event_date'],
    filters: rollupOrgFilter(orgCode),
    order: { 'DailyOrgRollup.event_date': 'asc' },
  });

  if (!row) return null;
  return String(row['DailyOrgRollup.event_date']).slice(0, 10);
}

/**
 * Whole-window reporting coverage ratio - deliberately NO `granularity` key (Rule 2).
 * Adding day granularity here would divide a per-day numerator by a full-window
 * denominator, silently under-reporting every day by roughly 1/N with no error.
 *
 * Null handling is defensive on two shapes (see plan M0 fixtures):
 *  - key present with a JSON `null` value - the real shape Cube returns when the
 *    `event_date` filter is dropped entirely (never happens here, since both bounds are
 *    always supplied by construction - see insightsDateRange.ts - but handled anyway).
 *  - key absent from the row entirely - not observed in practice, handled defensively in
 *    case Cube's behavior differs under other conditions.
 * A closed window that matches zero rows is NOT null - it is a real, correctly computed
 * `0` (valid seconds of 0 over a non-zero calendar-second denominator) - toRatio maps that
 * "0" string to the number `0`, not to `null`, and callers must render it as an honest 0%.
 */
export async function getReportingCoverage(
  orgCode: string,
  { from, to }: InsightsDateRange,
): Promise<number | null> {
  const [row] = await loadCube({
    measures: ['DailyOrgRollup.reporting_coverage_ratio', 'DailyOrgRollup.valid_reporting_seconds'],
    filters: rollupOrgFilter(orgCode),
    timeDimensions: [{ dimension: 'DailyOrgRollup.event_date', dateRange: [from, to] }],
  });

  return toRatio(row?.['DailyOrgRollup.reporting_coverage_ratio']);
}

const HOURS_PER_DAY = 24;

/** The "no telemetry" staleness threshold for `getUtilization`, in days - independent of
 * the page's selected date range (see that function's header comment, point 3). Exported
 * so display copy (`UtilizationProgressCard`'s `staleLineText`) can interpolate its day
 * count from this single source of truth instead of hardcoding "14" as literal text
 * disconnected from the actual threshold - if this value ever changes, the UI text updates
 * with it instead of silently going stale. */
export const NO_TELEMETRY_STALE_THRESHOLD_DAYS = 14;

/** `NO_TELEMETRY_STALE_THRESHOLD_DAYS`, expressed in hours to match
 * `DeviceLatestStatus.hours_since_last_event`'s unit. */
const NO_TELEMETRY_STALE_THRESHOLD_HOURS = NO_TELEMETRY_STALE_THRESHOLD_DAYS * HOURS_PER_DAY;

/**
 * Whole-window "device reporting hours" totals for the Protected Hours card. Two rules
 * this function must encode, not just document:
 *
 *  1. Sourced from `DailyDeviceRollup`, NOT `DailyOrgRollup`. `DailyOrgRollup`'s
 *     `protected_reporting_hours`/`valid_reporting_hours` are org-minute grain - they count
 *     an org-minute once no matter how many devices reported in it, which massively
 *     undercounts a multi-device org. `DailyDeviceRollup`'s device-minute-grain hours add
 *     across concurrent devices, which is the correct measure for a "total hours" headline
 *     (see `DailyDeviceRollup.js`'s header comment, design rank 2b).
 *  2. Labeling constraint: always "device reporting hours" in any UI copy fed by this
 *     function - never "work hours" or "logged/scheduled field hours". This measures time
 *     a Compass was powered on and paired via a phone, not scheduled shift time (see
 *     `UtilizationSection.tsx`'s header comment and the sibling repo's
 *     `docs/guides/navigator-analytics-catalog.md` "Never say" table).
 *
 * Day-grain rows are requested (matching `getComplianceTrend`'s pattern) purely so the two
 * window sums can be reduced with `sumMeasure` across the whole range; no day-by-day points
 * are returned since only the two headline hour totals are needed by the Protected Hours
 * card. A closed window that matches zero rows sums to a real `0` for both fields, not
 * `null` - same "zero rows is a genuine zero" rule `getReportingCoverage` already documents,
 * not the divide-by-zero null `sumRatio` uses for ratios (there is no ratio here).
 */
export async function getProtectedHours(
  orgCode: string,
  { from, to }: InsightsDateRange,
): Promise<ProtectedHoursSummary> {
  const rows = await loadCube({
    measures: [
      'DailyDeviceRollup.valid_reporting_hours',
      'DailyDeviceRollup.protected_reporting_hours',
    ],
    filters: dailyDeviceRollupOrgFilter(orgCode),
    timeDimensions: [
      { dimension: 'DailyDeviceRollup.event_date', dateRange: [from, to], granularity: 'day' },
    ],
    order: { 'DailyDeviceRollup.event_date': 'asc' },
  });

  return {
    validHours: sumMeasure(rows, 'DailyDeviceRollup.valid_reporting_hours'),
    protectedHours: sumMeasure(rows, 'DailyDeviceRollup.protected_reporting_hours'),
  };
}

/**
 * Device utilization for the selected window, plus an independent 14-day stale-device
 * count. Two Cube calls, fired in parallel (same shape as `getVersionDistribution`) and
 * folded into one result type, since both feed the same `UtilizationSection` card group.
 *
 *  1. Numerator (`devicesReportingInWindow`): distinct devices that reported at least once
 *     in the selected window, from `DailyDeviceRollup`. The query requests `device_key` as
 *     a `dimensions` entry with no `granularity` on the date-range `timeDimensions`,
 *     deliberately mirroring `getReportingCoverage`'s omission - only the window's row set
 *     matters here, not a day-by-day breakdown. Each returned row is already unique per
 *     `(org_code, event_date, device_key)`, so a device present on multiple days returns
 *     multiple rows; counting rows would double-count it. This de-duplicates with a
 *     `Set<string>` of device keys and takes its size instead.
 *  2. Denominator: intentionally NOT fetched here. `DeviceLatestStatus.device_count`
 *     (all-time cumulative) is already fetched by `getFleetStatus` and available as
 *     `FleetStatus.totalReportingDevices` - callers (`useInsightsData`/`UtilizationSection`)
 *     reuse that number instead of this function issuing a second, redundant Cube query
 *     for the same measure.
 *  3. `noTelemetryIn14Days`: counted client-side from `DeviceLatestStatus.
 *     hours_since_last_event`, a computed `dateDiff` dimension, not a stored column (see
 *     `DeviceLatestStatus.js`). This deliberately does NOT push a numeric `gt` filter down
 *     into Cube's `filters` against that computed dimension - that has not been confirmed
 *     to work cleanly in a real Cube query response, so the safe fallback is to fetch the
 *     dimension and threshold it here instead, against `NO_TELEMETRY_STALE_THRESHOLD_HOURS`
 *     (14 days in hours, matching the dimension's own hour unit).
 *
 * Both fields sum/count to a real `0` for a genuine zero-devices result, never `null` -
 * same "zero rows is a genuine zero" rule as `getReportingCoverage` and `getProtectedHours`.
 */
export async function getUtilization(
  orgCode: string,
  { from, to }: InsightsDateRange,
): Promise<UtilizationSummary> {
  const [windowRows, latestStatusRows] = await Promise.all([
    loadCube({
      dimensions: ['DailyDeviceRollup.device_key'],
      filters: dailyDeviceRollupOrgFilter(orgCode),
      timeDimensions: [{ dimension: 'DailyDeviceRollup.event_date', dateRange: [from, to] }],
    }),
    loadCube({
      dimensions: ['DeviceLatestStatus.device_key', 'DeviceLatestStatus.hours_since_last_event'],
      filters: orgFilter(orgCode),
    }),
  ]);

  const distinctDeviceKeysInWindow = new Set(
    windowRows.map((row) => String(row['DailyDeviceRollup.device_key'])),
  );

  const noTelemetryIn14Days = latestStatusRows.filter((row) => {
    const hoursSinceLastEvent = toNumber(row['DeviceLatestStatus.hours_since_last_event']);
    return hoursSinceLastEvent !== null && hoursSinceLastEvent > NO_TELEMETRY_STALE_THRESHOLD_HOURS;
  }).length;

  return {
    devicesReportingInWindow: distinctDeviceKeysInWindow.size,
    noTelemetryIn14Days,
  };
}

/**
 * Whole-window distinct Compass Core vs. Compass Pro device counts for the "Core vs. Pro
 * Devices" comparison card. This REPLACES that card's original metric (whole-window
 * `core_compliance_ratio`/`pro_compliance_ratio` percentages, sourced directly from Cube's
 * own class-bucketed ratio measures) with a plain count of distinct devices per class that
 * reported at least once in the selected window - "how many Core devices vs. how many Pro
 * devices", not "what share of protected time did each class have". The two metrics are not
 * interchangeable and this function's shape/reduction is entirely different from the ratio
 * version it replaced, not just a rename.
 *
 * Query shape - dimensions-only, row-level, no `measures` - deliberately mirrors
 * `getUtilization`'s own `windowRows` query (InsightsDAO.ts, above): `device_key` +
 * `is_core` as `dimensions`, filtered to the date range with NO `granularity` on the
 * `timeDimensions` entry (only the window's row set matters, not a day-by-day breakdown).
 * Each returned row is unique per `(org_code, event_date, device_key, is_core)` - `argMax
 * (is_core, event_ts)` per device-day per `DailyDeviceRollup.js` - so a device present on
 * multiple days returns multiple rows; counting rows would double-count it exactly the way
 * `getUtilization`'s own header comment describes. This reduces with the identical `Set<
 * string>`-of-`device_key`-per-bucket idiom `getUtilization` already uses for its single
 * fleet-wide number, just split into two buckets (Core/Pro) via `is_core` instead of
 * collapsed into one - reusing that exact reduction shape rather than inventing a new one.
 *
 * `is_core` -> class mapping reuses `coerceIsCompassCore` (below) verbatim - the same
 * `1 -> true` (Core) / `0 -> false` (Pro) / null -> `null` (undetermined) coercion rule
 * `getFirmwareDeviceDetails` already applies to this identical raw dimension, so a device's
 * class is sourced identically everywhere in this file, not re-derived per caller.
 *
 * DECISION - undetermined class (`is_core` null on every row a device contributed this
 * window): excluded from BOTH counts, no third "unknown class" bucket on this two-sided
 * card. Two reasons, not a coin flip:
 *  1. Consistency with this card's own sibling metrics on the same cube - the ratio version
 *     of this card (`core_compliance_ratio`/`pro_compliance_ratio`) already excludes
 *     unknown-class time from both its numerator and denominator by construction (each
 *     ratio is filtered to its own class), so a device with unknown class already
 *     contributed to neither side of the *previous* version of this exact card either. This
 *     count version preserves that same "unknown class means excluded from this Core/Pro
 *     framing entirely" precedent rather than introducing a new, inconsistent rule.
 *  2. It is a REAL possibility, not a theoretical one, and checked rather than assumed: per
 *     `docs/guides/2026-09-02-mute-ratio-unknown-class-issue.md`'s per-org `is_core IS NULL`
 *     prevalence table, several dev/test orgs show up to 100% NULL-class events (`AUSTEN21`
 *     100%, `DTESTING` ~90%, `SAFETY12` ~11%, `SAFEGDEV` ~1%), traced to legacy
 *     `schema_ver = "1"` telemetry payloads that omit the `is_core` key entirely - a
 *     historical/capture-era artifact (most recent occurrence April 2026 per that doc), not
 *     a live-traffic concern, since every current app build always sends `is_core`. Because
 *     this is real, `CoreProDeviceCountSummary`'s own header comment states the consequence
 *     plainly rather than burying it: `coreDeviceCount + proDeviceCount` is NOT guaranteed
 *     to equal the window's total distinct-device count - a device seen only with
 *     undetermined class in range is invisible to this card, exactly as it already was to
 *     the ratio version.
 *
 * Zero-of-one-class handling: unlike the ratio version this replaces (whose zero-denominator
 * case produced a real Cube-side JSON `null` via `nullIf`), THIS is a plain distinct-count
 * reduction with no division anywhere - there is no `nullIf`/divide-by-zero to trigger, so
 * there is no `null` case to normalize at all. A class with zero devices reporting in the
 * window simply has an empty `Set` (size `0`) - see the next paragraph for why Cube never
 * hands this function a reason to think otherwise. `0` is returned as a real, meaningful
 * count (Rule 1's *other* direction: a genuine zero must never be suppressed as if it were
 * missing data), which is exactly why `CoreProDeviceCountSummary` types both fields as plain
 * `number`, not `number | null` - there is no null state for a caller to handle here.
 *
 * Whether Cube omits rows for a zero-matching class, checked rather than assumed: this is a
 * `dimensions`-only query (no `measures`), so Cube returns exactly one row per unique
 * `(device_key, is_core)` combination actually present in the matching data for the window -
 * never a synthetic placeholder row for a dimension value that has zero matching rows (that
 * is a `measures`-driven "fill missing group with 0" behavior, which does not apply to a
 * bare dimensions query). A class with zero devices in range therefore contributes zero rows
 * to `rows` above, not a row with a `0` count - and because both `Set`s below start empty and
 * only grow from rows actually observed, that "zero rows for this class" case already reduces
 * to the correct `0` by construction, with no explicit fallback/default needed to catch a
 * missing key the way a `measures`-based reduction might require.
 */
export async function getCoreProDeviceCounts(
  orgCode: string,
  { from, to }: InsightsDateRange,
): Promise<CoreProDeviceCountSummary> {
  const rows = await loadCube({
    dimensions: ['DailyDeviceRollup.device_key', 'DailyDeviceRollup.is_core'],
    filters: dailyDeviceRollupOrgFilter(orgCode),
    timeDimensions: [{ dimension: 'DailyDeviceRollup.event_date', dateRange: [from, to] }],
  });

  const coreDeviceKeys = new Set<string>();
  const proDeviceKeys = new Set<string>();

  for (const row of rows) {
    const deviceKey = String(row['DailyDeviceRollup.device_key']);
    const isCompassCore = coerceIsCompassCore(row['DailyDeviceRollup.is_core']);
    if (isCompassCore === true) {
      coreDeviceKeys.add(deviceKey);
    } else if (isCompassCore === false) {
      proDeviceKeys.add(deviceKey);
    }
    // `isCompassCore === null` (undetermined class) is added to neither Set - see this
    // function's own DECISION paragraph above for why that is a deliberate choice, not an
    // oversight.
  }

  return {
    coreDeviceCount: coreDeviceKeys.size,
    proDeviceCount: proDeviceKeys.size,
  };
}

/** Maps a raw Cube dimension value to itself when it is a non-empty string, or to
 * `UNKNOWN_VERSION_LABEL` otherwise (null, undefined, or empty string) - the one place this
 * null/empty-to-sentinel rule is encoded, shared by `versionBucket` (aggregate `c_fw`/
 * `app_ver` buckets) and `getFirmwareDeviceDetails` (row-level `c_fw`), so a raw empty
 * firmware string is never classified differently between the card's aggregate counts and
 * its "View details" dialog's per-device rows. */
function rawStringOrUnknown(raw: unknown): string {
  return typeof raw === 'string' && raw.length > 0 ? raw : UNKNOWN_VERSION_LABEL;
}

function versionBucket(
  rows: CubeRow[],
  versionKey: string,
  groupKey: string,
  groupLabel: (raw: unknown) => string,
): VersionBucket[] {
  return rows.map((row) => ({
    version: rawStringOrUnknown(row[versionKey]),
    group: groupLabel(row[groupKey]),
    count: toNumber(row['DeviceLatestStatus.device_count']) ?? 0,
  }));
}

/** `is_core` arrives coerced through `toNumber` (it can be the string `"1"`), then maps
 * `1 -> "Compass Core"`, `0 -> "Compass Pro"`. A null/undetermined value (no seed data,
 * legacy row) buckets as "Unknown" rather than being silently dropped. */
function isCoreLabel(raw: unknown): string {
  const coerced = toNumber(raw);
  if (coerced === 1) return 'Compass Core';
  if (coerced === 0) return 'Compass Pro';
  return UNKNOWN_VERSION_LABEL;
}

/** Same `is_core` -> `toNumber` coercion rule as `isCoreLabel` above (raw can arrive as the
 * string `"1"`/`"0"`, and a null/undetermined value must survive as `null`, never be
 * silently coerced to `false`), but returns the boolean/null fact itself rather than a
 * display *label* string. Deliberately not a call-through to `isCoreLabel`: that function's
 * contract is a label for the version-distribution card's grouping, a different consumer
 * with a different return shape - `getFirmwareDeviceDetails` (below) needs the raw fact to
 * build a `DashboardDeviceOverride.compassInfoSnapshot.is_compass_core` when a dialog row's
 * MAC is clicked to open the device-card overlay, not a string to bucket by. Also reused by
 * `getCoreProDeviceCounts` (above) for the exact same reason - that function needs the raw
 * three-state Core/Pro/undetermined fact per device-row to bucket a `Set<string>` by, not a
 * label, so it shares this helper rather than duplicating the coercion a third time. */
function coerceIsCompassCore(raw: unknown): boolean | null {
  const coerced = toNumber(raw);
  if (coerced === 1) return true;
  if (coerced === 0) return false;
  return null;
}

const IOS_MODEL_PREFIX = /^(iphone|ipad|ipod)/i;

/** `phone_type` is a raw device-model string, not a platform. No platform dimension
 * exists in DeviceLatestStatus, so it is derived here by model-name prefix. This is
 * a heuristic: a model string we do not recognize buckets as Unknown rather than
 * defaulting to Android, so an unrecognized future device can never silently inflate
 * one platform. Proper fix is a `platform` dimension in the cube model - out of
 * scope for this web-only slice. */
export function derivePlatformLabel(raw: unknown): string {
  if (typeof raw !== 'string' || raw.length === 0) return UNKNOWN_VERSION_LABEL;
  return IOS_MODEL_PREFIX.test(raw) ? 'iOS' : 'Android';
}

/** Folds `VersionBucket` rows down to one row per `(version, group)` pair, summing counts.
 * Relabeling `phone_type` model strings to a platform (`derivePlatformLabel`) collapses many
 * distinct models into the same group, so the same `(version, group)` pair can otherwise
 * appear multiple times in the array - `countFor`/`flattenVersionTotals` downstream already sum
 * duplicates correctly, but aggregating here keeps `VersionBucket[]`'s contract (one row per
 * pair) honest for every caller, not just the ones that happen to sum. */
function aggregateVersionBuckets(buckets: VersionBucket[]): VersionBucket[] {
  const totals = new Map<string, VersionBucket>();
  for (const bucket of buckets) {
    const key = `${bucket.version}\u0000${bucket.group}`;
    const existing = totals.get(key);
    if (existing) {
      existing.count += bucket.count;
    } else {
      totals.set(key, { ...bucket });
    }
  }
  return [...totals.values()];
}

/** Firmware (`c_fw` x `is_core`) and mobile app (`app_ver` x `phone_type`) version
 * distributions - two Cube calls, fired in parallel. Distribution-only, per Rule 8: this
 * returns raw counts per version and nothing else; no release-recency judgement is
 * computed anywhere in this feature. */
export async function getVersionDistribution(orgCode: string): Promise<VersionDistribution> {
  const [firmwareRows, mobileRows] = await Promise.all([
    loadCube({
      measures: ['DeviceLatestStatus.device_count'],
      dimensions: ['DeviceLatestStatus.c_fw', 'DeviceLatestStatus.is_core'],
      filters: orgFilter(orgCode),
    }),
    loadCube({
      measures: ['DeviceLatestStatus.device_count'],
      dimensions: ['DeviceLatestStatus.app_ver', 'DeviceLatestStatus.phone_type'],
      filters: orgFilter(orgCode),
    }),
  ]);

  return {
    firmware: versionBucket(
      firmwareRows,
      'DeviceLatestStatus.c_fw',
      'DeviceLatestStatus.is_core',
      isCoreLabel,
    ),
    mobile: aggregateVersionBuckets(
      versionBucket(
        mobileRows,
        'DeviceLatestStatus.app_ver',
        'DeviceLatestStatus.phone_type',
        derivePlatformLabel,
      ),
    ),
  };
}

/**
 * Row-level firmware detail for the Firmware Version Distribution card's "View details"
 * dialog - one row per device (`device_key`, `c_mac`, `c_fw`), no `measures`, mirroring
 * `getUtilization`'s stale-device query shape (dimensions-only, row-level, no aggregation).
 * Filtered with the same `orgFilter` `getVersionDistribution`'s firmware query already uses
 * (both read `DeviceLatestStatus`), so this dialog's device set is exactly the same fleet the
 * card's aggregate counts are drawn from.
 *
 * `c_mac` (not `c_hw_id`) is fetched as the device identifier - a live Cube investigation
 * confirmed `c_hw_id` is populated in 0 of 10 sampled rows for devices stuck at the bootloader
 * sentinel (65% fleet-wide), while `c_mac` is populated in 9 of 10 sampled rows (97%
 * fleet-wide). `device_key` was observed to equal `c_mac` in every sampled row where both were
 * populated, but that is not relied on here - `device_key`'s computation is a pipeline
 * implementation detail, not a guaranteed contract, so `c_mac` is fetched as its own explicit
 * dimension rather than reusing `device_key` under an equivalence assumption. `c_hw_id` itself
 * is no longer fetched at all: nothing in this feature displays or classifies on it (YAGNI).
 *
 * `c_fw` is normalized through `rawStringOrUnknown` - the same null/empty-to-
 * `UNKNOWN_VERSION_LABEL` mapping `versionBucket` applies for the card's aggregate buckets -
 * so a caller classifying these rows (see `FirmwareDetailsDialog`'s `classifyFirmwareDevices`)
 * can compare `firmwareVersion === UNKNOWN_VERSION_LABEL` directly rather than re-deriving the
 * sentinel from a raw null/empty check of its own. This is what keeps the dialog's "No
 * reported firmware" row count identical to `computeFirmwareCurrency`'s `unknownCount` for
 * the same underlying data. `macAddress` is normalized through the same helper (Rule 1): a
 * genuinely empty `c_mac` becomes `UNKNOWN_VERSION_LABEL`, which the dialog maps to its own
 * device-identity fallback label rather than rendering an empty string.
 *
 * `is_core` is also fetched here - the exact same `DeviceLatestStatus.is_core` dimension
 * `getVersionDistribution`'s firmware query already queries, reused rather than invented,
 * so a device's Core/Pro classification is sourced identically whether it's read from the
 * aggregate distribution or this row-level dialog. It's coerced with `coerceIsCompassCore`
 * (not `isCoreLabel` - see that function's own doc comment) so a MAC clicked in the dialog
 * can open the same device-card overlay (`UserDashboard`) a Device Management row click
 * opens, with a real `is_compass_core` fact rather than a guess (see
 * `FirmwareDetailsDialog.buildFirmwareDeviceOverride`).
 */
export async function getFirmwareDeviceDetails(orgCode: string): Promise<FirmwareDeviceRow[]> {
  const rows = await loadCube({
    dimensions: [
      'DeviceLatestStatus.device_key',
      'DeviceLatestStatus.c_mac',
      'DeviceLatestStatus.c_fw',
      'DeviceLatestStatus.is_core',
    ],
    filters: orgFilter(orgCode),
  });

  return rows.map((row) => ({
    deviceKey: rawStringOrUnknown(row['DeviceLatestStatus.device_key']),
    macAddress: rawStringOrUnknown(row['DeviceLatestStatus.c_mac']),
    firmwareVersion: rawStringOrUnknown(row['DeviceLatestStatus.c_fw']),
    isCompassCore: coerceIsCompassCore(row['DeviceLatestStatus.is_core']),
  }));
}

/** Row cap for `getWorkZoneBreaches` (design §11.2). A dimensions-only, row-level event
 * query is unbounded by construction - expected volume is near zero at launch (PRD §1.1),
 * but "near zero at launch" is not a design. One extra row is requested beyond this cap
 * (see `getWorkZoneBreaches` below) purely to detect truncation without a second
 * round trip - the cap itself is the number of rows ever returned to a caller. */
export const WORK_ZONE_BREACH_ROW_LIMIT = 500;

/** `value` is both non-null and a real, finite number - the guard `getWorkZoneBreaches`'s
 * location mapping needs on an already-`toNumber`-coerced value. Factored out so the two
 * coordinate checks in `buildWorkZoneBreachLocation` read as one condition each rather than
 * a repeated `!== null && Number.isFinite(...)` pair, and so `Number.isFinite` - which is
 * typed to take a `number`, not `number | null` - never needs a null argument. */
function isFiniteCoordinate(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

/**
 * Builds `WorkZoneBreach.location` from raw Cube dimension values. This is the single place
 * the null-location contract (PRD D11/D13/R15, design §4.4/§11.2) can be silently lost, so
 * it is written out explicitly rather than folded into an inline ternary:
 *
 *  - `location` is built ONLY when BOTH the run-end latitude and longitude coerce to
 *    finite numbers. Otherwise the result is `null` - never a half-built object.
 *  - NEVER `?? 0`, NEVER `Number(x) || 0`, NEVER a falsy check (`if (lat)`) on either
 *    coordinate. `0` is a legal latitude (the equator) AND a legal longitude (the prime
 *    meridian): a falsy check silently drops real, valid equatorial positions, and `?? 0`/
 *    `|| 0` FABRICATES a position the device never actually reported. Both are correctness
 *    bugs on a safety surface, not style preferences - the measured location-null rate is
 *    35.71% (up to ~99.98% in one org), so this null path is the COMMON case, not an edge case.
 *  - Cube's ClickHouse driver returns numeric dimension values as JSON STRINGS over the
 *    wire, exactly like every other numeric field in this file - so raw values are coerced
 *    with the existing `toNumber` helper FIRST, and only the coerced result is checked with
 *    `Number.isFinite` (via `isFiniteCoordinate`). `typeof x === 'number'` is never a valid
 *    presence/validity check here, since the raw wire value is a string before coercion.
 *  - `accuracyMeters` maps independently of the coordinate pair and stays `null` when
 *    absent, matching `run_end_loc_acc_m`'s independent nullability in the mart (design §4.2).
 */
function buildWorkZoneBreachLocation(
  rawLatitude: unknown,
  rawLongitude: unknown,
  rawAccuracyMeters: unknown,
): WorkZoneBreachLocation | null {
  const latitude = toNumber(rawLatitude);
  const longitude = toNumber(rawLongitude);

  if (!isFiniteCoordinate(latitude) || !isFiniteCoordinate(longitude)) {
    return null;
  }

  return {
    latitude,
    longitude,
    accuracyMeters: toNumber(rawAccuracyMeters),
  };
}

/**
 * Row-level Work Zone Breach query (PRD D8; design §11.2) feeding `WorkZoneAdaptCard` -
 * dimensions-only, no `measures` (the plan wants raw breach rows, not aggregates, for this
 * function), mirroring the row-level query shape `getFirmwareDeviceDetails` and
 * `getCoreProDeviceCounts` already use in this file.
 *
 * `timeDimensions` on `run_start_date` carries NO `granularity` (only the window's row set
 * matters, same omission `getUtilization`/`getCoreProDeviceCounts` already make), and
 * `order` is by `run_start_ts` descending so the most recent breaches lead.
 *
 * Transport neutrality: this routes through the existing `loadCube` exactly like every
 * other function in this file - no second transport, no bespoke fetch, no dev/prod branch.
 *
 * `coerceIsCompassCore` is deliberately NOT used here - device class is an operations/
 * diagnostic signal recorded on the mart (design §4.7), not a field on this UI-facing
 * result (design §11.1) - see `WorkZoneBreach`'s own header comment in InsightsTypes.ts for
 * why surfacing it here would reintroduce the sample-level class gate M2 removed.
 *
 * Row cap: requests one row beyond `WORK_ZONE_BREACH_ROW_LIMIT` so truncation can be
 * detected and surfaced in `WorkZoneBreachQueryResult.truncated` rather than silently
 * dropping rows past the cap with no signal to the caller (design §11.2).
 *
 * `authId` / `distinctAuthIdCount` mapping (M3, user attribution feature; design §5.1) -
 * this DAO is the Cube boundary and does NOT resolve either field to a name or group:
 *  - `authId` is passed through as the plain string Cube returns, UNMODIFIED. `''` is a
 *    LEGITIMATE, meaningful value ("no user recorded on this row"), not missing/absent data
 *    to normalize away - never `?? ''` (redundant/misleading: `''` is already the correct
 *    value here, not a fallback), never coerced to `null`, never treated as falsy-and-
 *    therefore-absent. A coercion added here would silently break a future resolver's join.
 *  - `distinctAuthIdCount` goes through the existing `toNumber` coercion, mirroring
 *    `durationSeconds`/`sampleCount` immediately above: it is a non-nullable UInt32 mart
 *    column, but Cube's ClickHouse driver still returns it as a JSON string over the wire
 *    (the same trap `buildWorkZoneBreachLocation`'s comment documents at length), so
 *    `toNumber` runs first and `?? 0` guards the same not-actually-reachable-but-defensive
 *    null case `durationSeconds`/`sampleCount` already guard.
 *  - No name, no group, no roster import anywhere in this function or file - resolving
 *    `authId` is a separate layer's job, built over `OrganizationProvider`'s roster.
 */
export async function getWorkZoneBreaches(
  orgCode: string,
  { from, to }: InsightsDateRange,
): Promise<WorkZoneBreachQueryResult> {
  const rows = await loadCube({
    dimensions: [
      'WorkZoneBreach.channel',
      'WorkZoneBreach.c_mac',
      'WorkZoneBreach.device_key',
      'WorkZoneBreach.run_start_ts',
      'WorkZoneBreach.run_end_ts',
      'WorkZoneBreach.duration_seconds',
      'WorkZoneBreach.sample_count',
      'WorkZoneBreach.active_setting',
      'WorkZoneBreach.run_end_lat',
      'WorkZoneBreach.run_end_lon',
      'WorkZoneBreach.run_end_loc_acc_m',
      'WorkZoneBreach.c_fw',
      'WorkZoneBreach.run_end_auth_id',
      'WorkZoneBreach.distinct_auth_id_count',
    ],
    filters: workZoneBreachOrgFilter(orgCode),
    timeDimensions: [{ dimension: 'WorkZoneBreach.run_start_date', dateRange: [from, to] }],
    order: { 'WorkZoneBreach.run_start_ts': 'desc' },
    limit: WORK_ZONE_BREACH_ROW_LIMIT + 1,
  });

  const truncated = rows.length > WORK_ZONE_BREACH_ROW_LIMIT;
  const boundedRows = truncated ? rows.slice(0, WORK_ZONE_BREACH_ROW_LIMIT) : rows;

  const breaches: WorkZoneBreach[] = boundedRows.map((row) => {
    const channel = String(row['WorkZoneBreach.channel']) as WorkZoneBreachChannel;
    const macAddress = rawStringOrUnknown(row['WorkZoneBreach.c_mac']);
    const startTsUtc = String(row['WorkZoneBreach.run_start_ts']);

    return {
      // org:channel:mac:startTs - mirrors the mart's own concat() primary key
      // (design §7's `id` dimension) so this stays a stable React key.
      id: `${orgCode}:${channel}:${macAddress}:${startTsUtc}`,
      channel,
      macAddress,
      deviceKey: rawStringOrUnknown(row['WorkZoneBreach.device_key']),
      startTsUtc,
      endTsUtc: String(row['WorkZoneBreach.run_end_ts']),
      durationSeconds: toNumber(row['WorkZoneBreach.duration_seconds']) ?? 0,
      sampleCount: toNumber(row['WorkZoneBreach.sample_count']) ?? 0,
      activeSetting: toNumber(row['WorkZoneBreach.active_setting']),
      location: buildWorkZoneBreachLocation(
        row['WorkZoneBreach.run_end_lat'],
        row['WorkZoneBreach.run_end_lon'],
        row['WorkZoneBreach.run_end_loc_acc_m'],
      ),
      firmwareVersion: rawStringOrUnknown(row['WorkZoneBreach.c_fw']),
      // '' is a legitimate value here ("no user recorded"), never coerced away - see this
      // function's own header comment for the full rule.
      authId: String(row['WorkZoneBreach.run_end_auth_id']),
      distinctAuthIdCount: toNumber(row['WorkZoneBreach.distinct_auth_id_count']) ?? 0,
    };
  });

  return { breaches, truncated };
}
